/**
 * Design tokens do GEFSAN.
 *
 * NAO sao tokens novos: sao os do design system que a ARSAL ja usa. Extraidos de
 *   arsal-drive/frontend/src/@core/theme/{palette,typography,index}.ts   (templateName: 'SANEAMENTO')
 *   arsal-join-frontend/src/@core/theme/**                               (mesmo template, base Sneat)
 *
 * O primario e o azul institucional #02559b — nao o roxo #696CFF do template Sneat
 * cru, que o Join herdou mas o Drive ja substituiu pela identidade da agencia.
 *
 * Consumido pelo painel (MUI createTheme) e pelo app (react-native-paper). Os
 * mockups da GEFSAN sao dark: o app usa MODO_ESCURO daqui, que e o dark mode do
 * proprio design system da casa — nao um navy inventado.
 */

// ---------------------------------------------------------------------------
// Marca
// ---------------------------------------------------------------------------

// A chave `lighter` e o main a 20% de opacidade (sufixo '33'). Componentes Sneat
// (Alert soft, chips, avatares de status) leem essa variante; sem ela o MUI cai
// no fallback e a cor foge da identidade. Mesmos valores do arsal-join-frontend.
export const MARCA = {
  primaria: { lighter: '#02559b33', light: '#0267b9', main: '#02559b', dark: '#02457e', contrastText: '#FFF' },
  secundaria: { lighter: '#2dafe533', light: '#2dafe5', main: '#2dafe5', dark: '#2dade5', contrastText: '#FFF' },
} as const

/** Semanticas — identicas em Drive e Join, herdadas do Sneat. Nao mexer. */
export const SEMANTICAS = {
  erro: { lighter: '#FF3E1D33', light: '#FF5B3F', main: '#FF3E1D', dark: '#E8381A', contrastText: '#FFF' },
  alerta: { lighter: '#FFAB0033', light: '#FFB826', main: '#FFAB00', dark: '#E89C00', contrastText: '#FFF' },
  info: { lighter: '#03C3EC33', light: '#29CCEF', main: '#03C3EC', dark: '#03B1D7', contrastText: '#FFF' },
  sucesso: { lighter: '#71DD3733', light: '#86E255', main: '#71DD37', dark: '#67C932', contrastText: '#FFF' },
} as const

export const CINZAS = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
} as const

// ---------------------------------------------------------------------------
// Superficies
// ---------------------------------------------------------------------------

/** Base rgb do texto: o design system deriva texto/divider/action dela com alpha. */
const TEXTO_RGB_CLARO = '50, 71, 92'
const TEXTO_RGB_ESCURO = '219, 219, 235'

export const MODO_CLARO = {
  fundo: '#F5F5F9',
  superficie: '#FFFFFF',
  fundoTrilha: '#EBEEF0',
  fundoAvatar: '#F0EFF0',
  fundoCabecalhoTabela: '#F3F4F6',
  texto: `rgba(${TEXTO_RGB_CLARO}, 0.87)`,
  textoSecundario: `rgba(${TEXTO_RGB_CLARO}, 0.6)`,
  textoDesabilitado: `rgba(${TEXTO_RGB_CLARO}, 0.38)`,
  divisor: `rgba(${TEXTO_RGB_CLARO}, 0.12)`,
} as const

export const MODO_ESCURO = {
  fundo: '#232333',
  superficie: '#2B2C40',
  fundoTrilha: '#444463',
  fundoAvatar: '#3F3B59',
  fundoCabecalhoTabela: '#353649',
  texto: `rgba(${TEXTO_RGB_ESCURO}, 0.87)`,
  textoSecundario: `rgba(${TEXTO_RGB_ESCURO}, 0.6)`,
  textoDesabilitado: `rgba(${TEXTO_RGB_ESCURO}, 0.38)`,
  divisor: `rgba(${TEXTO_RGB_ESCURO}, 0.12)`,
} as const

export const FORMA = {
  /** shape.borderRadius do Drive e do Join */
  raio: 6,
  /** spacing(factor) => 0.25rem * factor — em RN, 4 * factor px */
  unidadeEspacamento: 4,
} as const

export const TIPOGRAFIA = {
  familia: 'Public Sans',
  familiaFallback:
    'Public Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  pesoTitulo: 500,
} as const

// ---------------------------------------------------------------------------
// Tokens de dominio — mapeados NA paleta acima, sem cor nova
// ---------------------------------------------------------------------------

/** Respostas do checklist (Tela 5) */
export const COR_RESPOSTA = {
  SIM: SEMANTICAS.sucesso.main,
  NAO: SEMANTICAS.erro.main,
  NA: CINZAS[500],
} as const

/** Conformidade calculada (relatorio e dashboards) */
export const COR_CONFORMIDADE = {
  conforme: SEMANTICAS.sucesso.main,
  nao_conforme: SEMANTICAS.erro.main,
  nao_aplicavel: CINZAS[500],
} as const

/** Badges de status da vistoria (Telas 4 e 8) */
export const COR_STATUS_VISTORIA = {
  rascunho: CINZAS[500],
  em_analise: SEMANTICAS.alerta.main,
  aguardando_aprovacao: SEMANTICAS.alerta.main,
  aprovada: SEMANTICAS.sucesso.main,
  devolvida: SEMANTICAS.erro.main,
  homologada: MARCA.primaria.main,
} as const

/**
 * Badge dos modulos da vistoria no Hub (Tela 4: Ficha de Inspecao / Fotos).
 * Aqui `pendente` e ambar (ainda nao preenchido) — diferente do `pendente`
 * da fila de auditoria (Tela 8), que e vermelho porque significa "falta vincular NCs".
 */
export const COR_STATUS_MODULO = {
  pendente: SEMANTICAS.alerta.main,
  em_analise: SEMANTICAS.info.main,
  cadastrado: SEMANTICAS.sucesso.main,
} as const

/** Ciclo de vida da NC (dropdown da Tela 9) */
export const COR_STATUS_NC = {
  pendente: SEMANTICAS.alerta.main,
  repasse: SEMANTICAS.info.main,
  justificado: SEMANTICAS.sucesso.main,
  sanado: SEMANTICAS.sucesso.dark,
  auto_infracao: SEMANTICAS.erro.dark,
} as const

/**
 * Qualidade probatoria da coordenada (dossie da auditoria e revisao do gerente).
 *
 * `boa` e `regular` sustentam o processo — verde e info, sem alarme. Do `fraca`
 * para baixo o ponto nao localiza o ato, e a cor precisa dizer isso antes do
 * texto: e o unico aviso que o gerente recebe antes de dar o de acordo.
 */
export const COR_NIVEL_COORDENADA = {
  boa: SEMANTICAS.sucesso.main,
  regular: SEMANTICAS.info.main,
  fraca: SEMANTICAS.erro.main,
  /** digitada / geocodificada / centro do CEP: nao ha medicao por tras */
  declarada: SEMANTICAS.alerta.main,
  /** coordenada antiga, sem registro de precisao — desconhecida, nao boa */
  indeterminada: CINZAS[500],
  ausente: SEMANTICAS.alerta.main,
} as const

/** Indicador de conectividade no header (elemento global do app) */
export const COR_CONECTIVIDADE = {
  online: SEMANTICAS.sucesso.main,
  offline: SEMANTICAS.erro.main,
} as const
